import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Manages consultant data + the approval pipeline using real API data.
 * Internal consultants come from GET /users/ (role = Consultant).
 * Pending approvals are consultants whose consultant_status is Applied / Under Review.
 */
export const useConsultants = () => {
  const [internalConsultants, setInternalConsultants] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users/');
      const consultants = (data || []).filter(u => u.role === 'Consultant');
      setInternalConsultants(consultants.map(c => ({
        id: c.id,
        name: c.full_name,
        email: c.email,
        specialty: c.specialization || '—',
        status: c.consultant_status || 'Active',
      })));
      setPendingApprovals(
        consultants
          .filter(c => ['Applied', 'Under Review'].includes(c.consultant_status))
          .map(c => ({ id: c.id, profile: c, status: 'pending' }))
      );
    } catch {
      setInternalConsultants([]);
      setPendingApprovals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchInternal = (query) => {
    if (!query) return internalConsultants;
    const q = query.toLowerCase();
    return internalConsultants.filter(c =>
      c.name?.toLowerCase().includes(q) || c.specialty?.toLowerCase().includes(q)
    );
  };

  return {
    loading,
    internalConsultants,
    pendingApprovals,
    searchInternal,
    refresh: load,
  };
};
